import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../core/store/store';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import { useInterventionsList } from './hooks/useInterventionsList';
import type { InterventionCTA } from './hooks/useInterventionsList';
import { getApiBaseUrl } from '../../../core/config/api';
import { usePanelAccess } from '../context/PanelAccessContext';
import {
  SCENARIO_STATUS_OPTIONS,
  type ScenarioStatusValue,
  type InterventionHistoryEntry,
  normalizeScenarioStatus,
  scenarioStatusSelectRingClass,
  pickSpawnBuildingId,
  isScenarioReferentClient,
  formatScenarioDateTime,
  scenarioActionLabel,
} from './hooks/interventionsManageHelpers';

const InterventionsManage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { currentUser } = useSelector((state: RootState) => state.user);
  const { referentFunctions } = usePanelAccess();
  const canValidateActif = isScenarioReferentClient(referentFunctions);
  const { interventions, loading, error, reload } = useInterventionsList();
  const [spawnMsg, setSpawnMsg] = useState<string | null>(null);
  const [spawnBusy, setSpawnBusy] = useState<number | null>(null);
  const [statusBusyId, setStatusBusyId] = useState<number | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [historyOpenId, setHistoryOpenId] = useState<number | null>(null);
  const [historyById, setHistoryById] = useState<Record<number, InterventionHistoryEntry[]>>({});
  const [historyLoadingId, setHistoryLoadingId] = useState<number | null>(null);

  const changeScenarioStatut = async (intervention: InterventionCTA, newStatut: ScenarioStatusValue) => {
    const current = normalizeScenarioStatus(intervention.statut);
    if (newStatut === current) return;
    if (newStatut === 'actif' && !canValidateActif) {
      setStatusMsg('Seul le référent scénario peut valider un scénario en « Actif ».');
      return;
    }
    setStatusMsg(null);
    setStatusBusyId(intervention.id);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setStatusMsg('Session expirée — reconnectez-vous.');
        return;
      }
      const apiUrl = getApiBaseUrl();
      const res = await fetch(`${apiUrl}/api/cta/interventions/${intervention.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ statut: newStatut }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok && !data.error) {
        setStatusMsg(`Erreur HTTP ${res.status}`);
        return;
      }
      if (data.success) {
        await reload();
        setStatusMsg(null);
      } else {
        setStatusMsg(data.error || 'Impossible de mettre à jour le statut.');
      }
    } catch (e: unknown) {
      setStatusMsg(e instanceof Error ? e.message : 'Erreur réseau.');
    } finally {
      setStatusBusyId(null);
    }
  };

  const formatDateTime = formatScenarioDateTime;
  const actionLabel = scenarioActionLabel;

  const openHistoryModal = async (interventionId: number) => {
    setHistoryOpenId(interventionId);
    if (historyById[interventionId]) return;

    setHistoryLoadingId(interventionId);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setStatusMsg('Session expirée — reconnectez-vous.');
        return;
      }
      const apiUrl = getApiBaseUrl();
      const res = await fetch(`${apiUrl}/api/cta/interventions/${interventionId}/history`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setStatusMsg(data.error || `Impossible de charger l'historique (HTTP ${res.status})`);
        return;
      }
      setHistoryById((prev) => ({
        ...prev,
        [interventionId]: Array.isArray(data.data) ? data.data : [],
      }));
    } catch (e: unknown) {
      setStatusMsg(e instanceof Error ? e.message : "Erreur lors du chargement de l'historique.");
    } finally {
      setHistoryLoadingId(null);
    }
  };

  const spawnTestOnMyAccount = async (scenarioId: number) => {
    setSpawnMsg(null);
    setSpawnBusy(scenarioId);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setSpawnMsg('Session expirée — reconnectez-vous.');
        return;
      }
      const apiUrl = getApiBaseUrl();
      const pick = await pickSpawnBuildingId(token, apiUrl);
      if (!pick.id) {
        setSpawnMsg(pick.error || 'Pas de bâtiment éligible pour la génération.');
        return;
      }
      const res = await fetch(`${apiUrl}/api/cta/generate-intervention`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          scenario_id: scenarioId,
          departement_code: '75',
          player_building_id: pick.id,
          description_joueur: 'Test PIO1 — lancé depuis le panel (position aléatoire dans le secteur 5 km)',
        }),
      });
      const data = await res.json();
      if (data.success) {
        const num = data.data?.numero_intervention ?? data.data?.id;
        setSpawnMsg(
          `Instance créée (${num}). Ouvrez la carte en jeu : menu interventions à droite.`
        );
      } else {
        setSpawnMsg(data.error || 'Échec de la génération.');
      }
    } catch (e: unknown) {
      setSpawnMsg(e instanceof Error ? e.message : 'Erreur réseau');
    } finally {
      setSpawnBusy(null);
    }
  };

  return (
    <div className={embedded ? 'overflow-y-auto' : 'min-h-screen relative overflow-hidden'}>
      {!embedded && <BackgroundImage />}
      {!embedded && <ParticlesBackground />}
      {!embedded && <Navbar />}
      <div className={embedded ? '' : 'relative z-10 pt-24 pb-16'}>
        <div className={embedded ? 'max-w-7xl mx-auto' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'}>
          {!embedded ? (
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-5xl font-bold text-white mb-2 text-gradient">
                  Gérer les scénarios d'interventions
                </h1>
                <p className="text-gray-300">Liste et gestion des scénarios d'interventions CTA</p>
              </div>
              <div className="flex space-x-4">
                <button
                  onClick={() => navigate('/panel/interventions/create')}
                  className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl"
                >
                  Créer un scénario
                </button>
                <button
                  onClick={() => reload()}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-all"
                  title="Actualiser la liste"
                >
                  Actualiser
                </button>
                <button
                  onClick={() => navigate('/panel')}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-all"
                >
                  Retour
                </button>
              </div>
            </div>
          ) : (
            <div className="mb-4 flex justify-end gap-2">
              <button
                onClick={() => navigate('/panel/interventions/create')}
                className="rounded-lg border border-violet-500/40 bg-violet-500/15 px-4 py-2 text-xs font-medium text-violet-100 hover:bg-violet-500/25 transition-colors"
              >
                + Créer un scénario
              </button>
              <button
                onClick={() => reload()}
                className="rounded-lg border border-white/15 bg-black/30 px-4 py-2 text-xs font-medium text-slate-200 hover:bg-black/45 transition-colors"
                title="Actualiser la liste"
              >
                Actualiser
              </button>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              {error}
            </div>
          )}

          {spawnMsg && (
            <div
              className={`mb-4 p-4 rounded-lg border ${
                spawnMsg.startsWith('Instance créée')
                  ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-100'
                  : 'bg-amber-500/15 border-amber-500/50 text-amber-100'
              }`}
            >
              {spawnMsg}
            </div>
          )}

          {statusMsg && (
            <div className="mb-4 p-4 rounded-lg border bg-red-500/15 border-red-500/50 text-red-100">
              {statusMsg}
            </div>
          )}

          <div className="rounded-xl border border-[#243052]/90 bg-[#0d152b]/90 p-6 backdrop-blur-sm">
            {loading ? (
              <div className="text-center py-12 text-white text-sm">
                Chargement des scénarios...
              </div>
            ) : interventions.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">
                Aucun scénario d'intervention trouvé.
                <br />
                <button
                  onClick={() => navigate('/panel/interventions/create')}
                  className="mt-4 rounded-lg border border-[#3d4fdb]/40 bg-[#3d4fdb]/20 px-4 py-2 text-sm font-semibold text-[#8ea0f5] hover:bg-[#3d4fdb]/30 transition-colors"
                >
                  Créer le premier scénario
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-[#1e2d4d]">
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">ID</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Service</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Catégorie</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Statut</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Exceptionnelle</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Créé</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Dernière modif</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Validation</th>
                      <th className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interventions.map((intervention) => (
                      <tr key={intervention.id} className="border-b border-[#1e2d4d]/60 hover:bg-[#0d1b3a]/40 transition-colors">
                        {(() => {
                          const canEditScenario =
                            !!currentUser?.username &&
                            !!intervention.created_by_username &&
                            currentUser.username === intervention.created_by_username;
                          return (
                            <>
                        <td className="px-4 py-3 text-white">{intervention.id}</td>
                        <td className="px-4 py-3 text-white">{intervention.service.toUpperCase()}</td>
                        <td className="px-4 py-3 text-slate-200">{intervention.categorie}</td>
                        <td className="py-3">
                          {(() => {
                            const norm = normalizeScenarioStatus(intervention.statut);
                            return (
                              <select
                                value={norm}
                                disabled={statusBusyId === intervention.id}
                                onChange={(e) =>
                                  changeScenarioStatut(
                                    intervention,
                                    e.target.value as ScenarioStatusValue
                                  )
                                }
                                className={`min-w-[10.5rem] rounded-md border border-[#2a3a62] bg-[#0a132b] px-2 py-1.5 text-xs text-white focus:outline-none disabled:opacity-50 ${scenarioStatusSelectRingClass(norm)}`}
                                title="Changer le statut du scénario"
                                aria-label={`Statut du scénario ${intervention.id}`}
                              >
                                {SCENARIO_STATUS_OPTIONS.map((o) => (
                                  <option
                                    key={o.value}
                                    value={o.value}
                                    disabled={o.value === 'actif' && !canValidateActif}
                                  >
                                    {o.label}
                                    {o.value === 'actif' && !canValidateActif ? ' (référent scénario)' : ''}
                                  </option>
                                ))}
                              </select>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          {intervention.is_exceptionnelle === 1 ||
                          intervention.is_exceptionnelle === true ? (
                            <span className="rounded px-2 py-0.5 text-xs bg-amber-500/20 text-amber-300 border border-amber-500/40">Oui</span>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-300">
                          <div>{formatDateTime(intervention.created_at)}</div>
                          <div className="text-xs text-slate-500">{intervention.created_by_username || 'Inconnu'}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-300">
                          <div>{formatDateTime(intervention.last_modified_at || intervention.updated_at)}</div>
                          <div className="text-xs text-slate-500">{intervention.last_modified_by_username || 'Inconnu'}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-300">
                          <div>{formatDateTime(intervention.validated_at)}</div>
                          <div className="text-xs text-slate-500">{intervention.validated_by_username || '—'}</div>
                        </td>
                        <td className="px-4 py-3 flex flex-wrap gap-2">
                          <button
                            onClick={() => navigate(`/panel/interventions/edit/${intervention.id}`)}
                            disabled={!canEditScenario}
                            className={`rounded border px-3 py-1 text-xs font-medium transition-colors ${
                              canEditScenario
                                ? 'border-[#3d4fdb]/40 bg-[#3d4fdb]/20 text-[#8ea0f5] hover:bg-[#3d4fdb]/30'
                                : 'border-[#2b3a61] bg-[#0d1b3a]/80 text-slate-500 cursor-not-allowed opacity-70'
                            }`}
                            title={canEditScenario ? 'Modifier ce scénario' : 'Modification réservée au créateur du scénario'}
                          >
                            Modifier
                          </button>
                          <button
                            type="button"
                            disabled={spawnBusy === intervention.id}
                            onClick={() => spawnTestOnMyAccount(intervention.id)}
                            className="rounded border border-emerald-500/40 bg-emerald-500/20 px-3 py-1 text-xs font-medium text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-50 transition-colors"
                            title="Crée une instance sur votre compte (test carte)"
                          >
                            {spawnBusy === intervention.id ? '…' : 'Tester sur mon compte'}
                          </button>
                          <button
                            type="button"
                            disabled={historyLoadingId === intervention.id}
                            onClick={() => openHistoryModal(intervention.id)}
                            className="rounded border border-violet-500/40 bg-violet-500/20 px-3 py-1 text-xs font-medium text-violet-300 hover:bg-violet-500/30 disabled:opacity-50 transition-colors"
                          >
                            {historyLoadingId === intervention.id ? 'Chargement...' : 'Historique'}
                          </button>
                        </td>
                            </>
                          );
                        })()}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      {historyOpenId !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setHistoryOpenId(null)}
        >
          <div
            className="w-full max-w-4xl max-h-[85vh] overflow-hidden rounded-xl border border-[#243052]/90 bg-[#0a1128] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#1e2d4d] px-6 py-4">
              <h2 className="text-base font-semibold text-white">
                Historique complet de l'intervention #{historyOpenId}
              </h2>
              <button
                type="button"
                onClick={() => setHistoryOpenId(null)}
                className="rounded-lg border border-white/15 bg-black/30 px-3 py-1.5 text-sm text-slate-200 hover:bg-black/45 transition-colors"
              >
                Fermer
              </button>
            </div>

            <div className="max-h-[70vh] overflow-y-auto p-5">
              {(historyById[historyOpenId] || []).length === 0 ? (
                <div className="text-sm text-slate-400">
                  Aucun événement enregistré pour cette intervention.
                </div>
              ) : (
                <div className="space-y-2">
                  {(historyById[historyOpenId] || []).map((entry) => (
                    <div
                      key={entry.id}
                      className="rounded border border-[#2b3a61] bg-[#0d1b3a]/80 px-3 py-2 text-sm text-slate-200"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-white">{actionLabel(entry.action)}</span>
                        <span className="text-slate-500">•</span>
                        <span>{formatDateTime(entry.created_at)}</span>
                        <span className="text-slate-500">•</span>
                        <span>par <span className="text-white">{entry.actor_username || 'Inconnu'}</span></span>
                      </div>
                      {(entry.previous_status || entry.new_status) && (
                        <div className="text-xs text-slate-400 mt-1">
                          Statut: {entry.previous_status || '—'} → {entry.new_status || '—'}
                        </div>
                      )}
                      {(entry.details?.changed_fields?.length ?? 0) > 0 && (
                        <div className="text-xs text-slate-400 mt-1">
                          Champs modifiés: {entry.details?.changed_fields?.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterventionsManage;

